import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type {
  PerfStrategiesResponse,
  PerfStrategyDetail,
  PerfRecord,
  PerfPlanCacheStats,
  PerfCacheLayerStats,
  PerfSubtreeStats,
} from '../lib/api';
import { RefreshCw, Activity, ChevronRight, ChevronDown, AlertCircle, Zap } from 'lucide-react';

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1) return '<1 ms';
  return `${ms.toFixed(0)} ms`;
}

function fmtTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function HitRateBadge({ rate, label }: { rate: number; label: string }) {
  const pct = Math.round(rate * 100);
  const color =
    pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{label}</span>
        <span className="font-semibold text-gray-700">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function TotalMsBadge({ ms }: { ms: number | null | undefined }) {
  if (ms == null) return <span className="text-gray-400 text-xs">—</span>;
  const color =
    ms < 500
      ? 'bg-emerald-100 text-emerald-800'
      : ms < 2000
      ? 'bg-amber-100 text-amber-800'
      : 'bg-red-100 text-red-800';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold font-mono ${color}`}>
      {fmtMs(ms)}
    </span>
  );
}

function CacheLayerCard({ layer, label }: { layer: PerfCacheLayerStats; label: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        <span className="text-xs text-gray-400">
          {layer.entries}/{layer.max_entries} entries
        </span>
      </div>
      <HitRateBadge rate={layer.hit_rate} label="Hit Rate" />
      <div className="flex justify-between text-xs text-gray-500">
        <span>{layer.hits} hits</span>
        <span>{layer.misses} misses</span>
      </div>
    </div>
  );
}

function CacheStatsPanel({ stats }: { stats: PerfPlanCacheStats }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <CacheLayerCard layer={stats.plan_cache} label="Plan Cache" />
      <CacheLayerCard layer={stats.ast_cache} label="AST Cache" />
      <CacheLayerCard layer={stats.discovery_cache} label="Discovery Cache" />
    </div>
  );
}

function SubtreeStatsCard({ stats }: { stats: PerfSubtreeStats }) {
  const pct = Math.round((stats.duplication_ratio ?? 0) * 100);
  const color = pct > 50 ? 'text-amber-600' : 'text-emerald-600';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
      <h4 className="text-sm font-semibold text-gray-700">AST Subtree Analysis</h4>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-lg font-semibold text-gray-800">{stats.total_nodes ?? '—'}</div>
          <div className="text-xs text-gray-500">Total nodes</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-gray-800">{stats.unique_nodes ?? '—'}</div>
          <div className="text-xs text-gray-500">Unique nodes</div>
        </div>
        <div>
          <div className={`text-lg font-semibold ${color}`}>{pct}%</div>
          <div className="text-xs text-gray-500">Duplication</div>
        </div>
      </div>
    </div>
  );
}

const STAGE_COLUMNS: { key: keyof PerfRecord; label: string }[] = [
  { key: 'parse_ms', label: 'Parse' },
  { key: 'discovery_ms', label: 'Discovery' },
  { key: 'price_fetch_ms', label: 'Price Fetch' },
  { key: 'ast_eval_ms', label: 'AST Eval' },
  { key: 'polars_collect_ms', label: 'Polars Collect' },
  { key: 'total_ms', label: 'Total' },
];

const SUMMARY_STAGES: { key: keyof PerfRecord; label: string }[] = [
  { key: 'parse_ms', label: 'Parse' },
  { key: 'discovery_ms', label: 'Discovery' },
  { key: 'price_fetch_ms', label: 'Price' },
  { key: 'ast_eval_ms', label: 'AST' },
  { key: 'polars_collect_ms', label: 'Polars' },
];

function TimingTable({ records }: { records: PerfRecord[] }) {
  if (records.length === 0) {
    return <p className="text-sm text-gray-500 italic">No timing records available.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Run
            </th>
            {STAGE_COLUMNS.map((s) => (
              <th
                key={s.key}
                className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                {s.label}
              </th>
            ))}
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Tickers
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Dates
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {records.map((rec, idx) => {
            const ts = rec.timestamp ?? rec.ran_at;
            const isLatest = idx === 0;
            return (
              <tr
                key={idx}
                className={`transition-colors ${isLatest ? 'bg-emerald-50' : 'bg-white hover:bg-gray-50'}`}
              >
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {fmtTimestamp(ts)}
                </td>
                {STAGE_COLUMNS.map((s) => {
                  const val = rec[s.key] as number | undefined;
                  const isTotal = s.key === 'total_ms';
                  return (
                    <td
                      key={s.key}
                      className={`px-4 py-3 text-right font-mono whitespace-nowrap ${
                        isTotal ? 'font-semibold text-gray-800' : 'text-gray-600'
                      }`}
                    >
                      {fmtMs(val)}
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-right text-gray-600">{rec.n_tickers ?? '—'}</td>
                <td className="px-4 py-3 text-right text-gray-600">{rec.n_dates ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StrategyRow({
  hash,
  detail,
  onSelect,
  selected,
}: {
  hash: string;
  detail: PerfStrategyDetail | null | undefined;
  onSelect: (h: string) => void;
  selected: boolean;
}) {
  const records = detail?.records ?? [];
  const latest = records[0] ?? null;
  const latestTs = latest ? (latest.timestamp ?? latest.ran_at) : null;
  const latestTotal = latest?.total_ms ?? null;

  return (
    <button
      onClick={() => onSelect(hash)}
      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-left border ${
        selected
          ? 'bg-emerald-700 text-white shadow-md border-emerald-700'
          : 'bg-white border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Activity
          className={`w-4 h-4 flex-shrink-0 ${selected ? 'text-white' : 'text-emerald-600'}`}
        />
        <span className={`font-mono text-sm ${selected ? 'text-white' : 'text-gray-800'}`}>
          {hash.slice(0, 16)}…
        </span>
      </div>

      <div className="flex items-center gap-4 ml-4 flex-shrink-0">
        {latest && (
          <div className="hidden sm:flex items-center gap-3">
            {SUMMARY_STAGES.map((s) => {
              const val = latest[s.key] as number | undefined;
              return (
                <div key={s.key} className="text-right">
                  <div
                    className={`text-xs font-mono ${
                      selected ? 'text-emerald-100' : 'text-gray-500'
                    }`}
                  >
                    {fmtMs(val)}
                  </div>
                  <div className={`text-xs ${selected ? 'text-emerald-200' : 'text-gray-400'}`}>
                    {s.label}
                  </div>
                </div>
              );
            })}
            <div className="w-px h-8 bg-gray-200 mx-1" />
            <div className="text-right">
              <TotalMsBadge ms={latestTotal} />
              <div
                className={`text-xs mt-0.5 ${selected ? 'text-emerald-200' : 'text-gray-400'}`}
              >
                {fmtTimestamp(latestTs)}
              </div>
            </div>
          </div>
        )}

        {!latest && detail !== null && detail !== undefined && (
          <span className="text-xs text-gray-400 italic">No runs</span>
        )}
        {detail === null && (
          <RefreshCw className="w-3 h-3 text-gray-300 animate-spin" />
        )}

        {selected ? (
          <ChevronDown className="w-4 h-4 text-white" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </div>
    </button>
  );
}

function StrategyDetailPanel({
  hash,
  detail,
  error,
}: {
  hash: string;
  detail: PerfStrategyDetail | null | undefined;
  error: string | null;
}) {
  if (error) {
    return (
      <div className="mt-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg px-4 py-3">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (detail === null) {
    return (
      <div className="mt-3 flex items-center gap-3 text-gray-500 text-sm py-4">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Loading timing data…
      </div>
    );
  }

  if (!detail) return null;

  const records = detail.records;
  const latestSubtreeStats = records.find((r) => r.subtree_stats)?.subtree_stats;

  return (
    <div className="mt-3 space-y-5">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Zap className="w-3 h-3" />
        <span className="font-mono">{hash}</span>
        <span>·</span>
        <span>{detail.record_count} run(s) recorded</span>
      </div>

      {latestSubtreeStats && <SubtreeStatsCard stats={latestSubtreeStats} />}

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Recent Run Timings{' '}
          <span className="font-normal text-gray-400">
            (newest first, most recent row highlighted)
          </span>
        </h3>
        <TimingTable records={records} />
      </div>
    </div>
  );
}

interface StrategyDetailMap {
  [hash: string]: PerfStrategyDetail | null;
}

export function PerformanceAdmin() {
  const [strategies, setStrategies] = useState<PerfStrategiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [details, setDetails] = useState<StrategyDetailMap>({});
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});

  const [selectedHash, setSelectedHash] = useState<string | null>(null);

  const fetchAllDetails = useCallback(async (hashes: string[]) => {
    if (hashes.length === 0) return;
    setDetails((prev) => {
      const next: StrategyDetailMap = { ...prev };
      for (const h of hashes) {
        if (!(h in next)) next[h] = null;
      }
      return next;
    });

    await Promise.all(
      hashes.map(async (hash) => {
        try {
          const data = await api.getPerfStrategyDetail(hash);
          setDetails((prev) => ({ ...prev, [hash]: data }));
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Failed to load';
          setDetailErrors((prev) => ({ ...prev, [hash]: msg }));
          setDetails((prev) => ({
            ...prev,
            [hash]: {
              strategy_hash: hash,
              record_count: 0,
              records: [],
              plan_cache_stats: {
                ast_cache: { name: 'ast', entries: 0, max_entries: 0, hits: 0, misses: 0, hit_rate: 0 },
                discovery_cache: { name: 'discovery', entries: 0, max_entries: 0, hits: 0, misses: 0, hit_rate: 0 },
                plan_cache: { name: 'plan', entries: 0, max_entries: 0, hits: 0, misses: 0, hit_rate: 0 },
              },
              db_backtest_runs: [],
            },
          }));
        }
      })
    );
  }, []);

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getPerfStrategies();
      setStrategies(data);
      setDetails({});
      setDetailErrors({});
      await fetchAllDetails(data.strategy_hashes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load strategy list');
    } finally {
      setLoading(false);
    }
  }, [fetchAllDetails]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  const handleSelect = useCallback((hash: string) => {
    setSelectedHash((prev) => (prev === hash ? null : hash));
  }, []);

  const handleRefresh = useCallback(async () => {
    setSelectedHash(null);
    await fetchStrategies();
  }, [fetchStrategies]);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Strategy Performance</h1>
          <p className="text-sm text-gray-500 mt-1">
            Per-run stage timings and cache hit rates for the evaluation engine
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && strategies && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800">Process-Wide Cache Stats</h2>
          <CacheStatsPanel stats={strategies.plan_cache_stats} />
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-1">
          Known Strategies{' '}
          {strategies && (
            <span className="text-sm font-normal text-gray-500">
              ({strategies.strategy_hashes.length} recorded)
            </span>
          )}
        </h2>
        <p className="text-xs text-gray-400 mb-4">
          Each row shows last-run stage timings. Total badge color: green &lt;500 ms · amber &lt;2 s ·
          red ≥2 s. Click a row to expand full history.
        </p>

        {loading && (
          <div className="flex items-center gap-3 text-gray-500 text-sm">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        )}

        {!loading && strategies && strategies.strategy_hashes.length === 0 && (
          <p className="text-sm text-gray-500 italic">
            No strategies have been evaluated yet. Run a backtest or rebalance to populate timing
            data.
          </p>
        )}

        {!loading && strategies && strategies.strategy_hashes.length > 0 && (
          <div className="space-y-2">
            {strategies.strategy_hashes.map((hash) => (
              <div key={hash}>
                <StrategyRow
                  hash={hash}
                  detail={details[hash]}
                  onSelect={handleSelect}
                  selected={selectedHash === hash}
                />
                {selectedHash === hash && (
                  <div className="mt-1 ml-4 pl-4 border-l-2 border-emerald-200 pb-2">
                    <StrategyDetailPanel
                      hash={hash}
                      detail={details[hash]}
                      error={detailErrors[hash] ?? null}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
